import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifyError } from "@/shared/lib";
import { mutator } from '@/shared/api';
import { handleSuccess } from '@/shared/lib/success';
import { userKeys } from './queryKeys';

export function useUserCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => mutator('POST', '/users', data),
    onSuccess: (_data, variables) => {
      // POST /users sends an invitation — nobody is "created" from the
      // merchant's side; the person shows as Pending until they accept. The
      // backend's fixed English "User invited successfully" is dropped (an
      // empty response object) so the translated copy, which can name the
      // address, is what shows.
      const email = typeof variables?.email === 'string' ? variables.email.trim() : '';
      handleSuccess({}, {
        titleKey: 'success.user.inviteSent',
        descriptionKey: email ? 'success.user.inviteSentTo' : 'success.user.inviteSentPending',
        params: { email },
      });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useUserUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      mutator('PATCH', `/users/${id}`, payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.updated' });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.details() });
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      mutator('POST', 'auth/forgot-password', payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.passwordResetRequested' });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useUserChangeStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      mutator('PUT', `/users/${id}/status`, { status }),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.statusUpdated' });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.details() });
    },
  });
}

/**
 * Send a pending member a fresh invitation.
 *
 * The backend issues a NEW link with a new expiry and keeps the membership —
 * and so the role — as it is (`user.service.ts → resendInvite`). That makes
 * this the way out of an expired invite too: re-inviting the same email is
 * refused ("User already invited to this store"), so before this the only
 * route was delete-and-re-invite, which also dropped the role.
 *
 * Needs `user.create`, the same permission as inviting.
 */
export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, storeId }: { userId: string; storeId: string }) =>
      mutator('POST', `/users/${userId}/stores/${storeId}/resend-invite`),
    onSuccess: () => {
      // Same as the invite: the backend line ("Invitation resent
      // successfully") is fixed English and repeats the title.
      handleSuccess({}, {
        titleKey: 'success.user.inviteResent',
        descriptionKey: 'success.user.inviteResentBody',
      });
      // invitedAt / expiry change on the membership.
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.details() });
    },
  });
}

export function useUserDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutator('DELETE', `/users/${id}`),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.deleted' });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export function useRequestMfaReset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutator('POST', `/users/${id}/mfa/reset`),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.mfaResetRequested' });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

export type RegisterPayload = { name: string; email: string };

export function useRegisterAccount() {
  return useMutation({
    mutationFn: (payload: RegisterPayload) => mutator('POST', '/auth/register', payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.accountCreatedSuccessfully' });
    },
    onError: (error: unknown) => {
      const resp = (error as { response?: { data?: { errors?: Array<{ field: string; message: string | string[] }>; message?: string } } })?.response?.data;
      const errors = resp?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        const general = errors.filter((e) => e.field === 'general' || e.field?.includes('general'));
        if (general.length > 0) {
          const msg = Array.isArray(general[0].message) ? general[0].message.join(', ') : general[0].message;
          notifyError({ title: resp?.message ?? 'Registration Failed', description: msg });
        } else {
          const allMsgs = errors.map((e) => Array.isArray(e.message) ? e.message.join(', ') : e.message).join(', ');
          notifyError({ title: 'Registration Failed', description: allMsgs });
        }
      } else {
        notifyError({
          title: 'Registration Failed',
          description: resp?.message ?? 'An unexpected error occurred. Please try again.'
        });
      }
    },
  });
}

export function useUpdateProfile() {
  return useMutation({
    mutationFn: (data: unknown) => mutator('PUT', '/auth/profile', data),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: 'success.user.profileUpdated' });
    },
  });
}

export function useSendPasswordResetLink() {
  return useMutation({
    mutationFn: (email: string) =>
      mutator('POST', '/auth/forgot-password', { email }),
  });
}
